/**
 * AssignmentPaidCelebration — the "cha-ching" moment.
 *
 * Fires when a Transaction Board card lands in the `assignment_paid` lane.
 * Two staggered confetti bursts (indigo + amber to match the brand),
 * then a warm-cream modal with the deal receipt + next-move CTAs.
 *
 * Usage:
 *   const [paidCard, setPaidCard] = useState(null);
 *   <AssignmentPaidCelebration card={paidCard} onClose={() => setPaidCard(null)} />
 *
 * Deliberately dependency-light — only leans on canvas-confetti (already
 * in the bundle) and lucide-react.
 */
import { useEffect } from "react";
import confetti from "canvas-confetti";
import { Link } from "react-router-dom";
import {
  Trophy, DollarSign, Building2, User, PartyPopper, X as XIcon,
  ArrowUpRight, Share2,
} from "lucide-react";


const fmtMoney = (n) => {
  const num = Number(n) || 0;
  if (!num) return "$0";
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(num >= 10_000 ? 0 : 1)}k`;
  return `$${num.toFixed(0)}`;
};


function fireConfetti() {
  // Staggered two-burst celebration — indigo primary, amber accent.
  // Matches the app's new Indigo/Amber palette (2026-02 rebrand).
  const base = { particleCount: 120, startVelocity: 55, spread: 80, ticks: 220, scalar: 1.1 };
  confetti({ ...base, origin: { x: 0.15, y: 0.6 }, angle: 55,
             colors: ["#4F46E5", "#818CF8", "#F59E0B", "#FEF3C7"] });
  confetti({ ...base, origin: { x: 0.85, y: 0.6 }, angle: 125,
             colors: ["#4F46E5", "#A5B4FC", "#F59E0B", "#FDE68A"] });
  // Overhead sprinkle a moment later so the modal has motion behind it.
  setTimeout(() => {
    confetti({
      particleCount: 60, spread: 160, startVelocity: 25,
      ticks: 260, origin: { x: 0.5, y: 0.35 }, gravity: 0.7,
      colors: ["#4F46E5", "#F59E0B", "#10B981"],
    });
  }, 350);
}


export default function AssignmentPaidCelebration({ card, onClose }) {
  useEffect(() => {
    if (!card) return;
    fireConfetti();
    // Auto-dismiss guard: escape key closes the modal.
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [card, onClose]);

  if (!card) return null;

  const dealHref = card.property_id ? `/v3/deal/${card.property_id}` : null;
  const fee = Number(card.assignment_fee) || 0;

  return (
    <div
      className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="assignment-paid-celebration"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="assignment-paid-card"
      >
        {/* Gradient header */}
        <div
          className="px-6 py-6 text-white relative"
          style={{ backgroundImage: "linear-gradient(135deg, #4F46E5 0%, #4338CA 55%, #F59E0B 130%)" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-white/70 hover:text-white p-1"
            data-testid="assignment-paid-close"
            aria-label="Close celebration"
          >
            <XIcon className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-black bg-white/15 px-2 py-1 rounded-full">
            <PartyPopper className="w-3 h-3" /> Assignment Paid
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
              <Trophy className="w-6 h-6 text-amber-200" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-bold text-white/70">
                You just closed
              </div>
              <div className="text-3xl font-black leading-tight tabular-nums" data-testid="assignment-paid-fee">
                {fmtMoney(fee)}
              </div>
            </div>
          </div>
        </div>

        {/* Deal receipt */}
        <div className="p-6 space-y-3">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2">
            <div className="flex items-start gap-2 text-[13px] text-slate-800">
              <Building2 className="w-3.5 h-3.5 text-indigo-500 mt-0.5 shrink-0" />
              <span className="font-semibold leading-tight line-clamp-2">{card.property_address}</span>
            </div>
            <div className="flex items-center gap-2 text-[12.5px] text-slate-600">
              <User className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
              <span className="truncate">{card.buyer_name}</span>
            </div>
            <div className="flex items-center justify-between text-[11.5px] text-slate-500 tabular-nums pt-1 border-t border-slate-200">
              <span className="inline-flex items-center gap-1">
                <DollarSign className="w-3 h-3" /> Fee
              </span>
              <span className="font-black text-emerald-700">{fmtMoney(fee)}</span>
            </div>
            {Number(card.purchase_price) > 0 && (
              <div className="flex items-center justify-between text-[11.5px] text-slate-500 tabular-nums">
                <span>Purchase price</span>
                <span className="font-semibold text-slate-700">{fmtMoney(card.purchase_price)}</span>
              </div>
            )}
          </div>

          <p className="text-[12.5px] text-slate-600 leading-relaxed">
            Log the lesson while it's fresh — one line about what made this
            deal close beats a dozen post-mortems later.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            {dealHref && (
              <Link
                to={dealHref}
                onClick={onClose}
                className="flex-1 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-black text-white rounded-lg py-2 px-3"
                style={{ backgroundImage: "linear-gradient(135deg, #6366F1, #4F46E5)" }}
                data-testid="assignment-paid-open-deal"
              >
                Open deal card <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            )}
            <Link
              to="/v3/growth/wins"
              onClick={onClose}
              className="flex-1 inline-flex items-center justify-center gap-1.5 text-[12.5px] font-black rounded-lg py-2 px-3 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
              data-testid="assignment-paid-publish-win"
            >
              <Share2 className="w-3.5 h-3.5" />
              Publish to Wins
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
